import _ from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { withStyles } from '@material-ui/core/styles';

import Paper from '@material-ui/core/Paper';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { Typography } from '@material-ui/core';

import SuperiorSubmissionsContainer from '../../containers/billing/SuperiorSubmissions';
import AuthUserContainer from '../../containers/AuthUser';

import { getSharedStyles } from '../../themes';
import EnhancedTable from '../../components/table/EnhancedTable';
import ResourcePopup from '../../components/Dialogs/ResourcePopup';
import { green, grey, orange, red, blue } from '@material-ui/core/colors';

const statusToDisplayMap = {
	received: 'Received',
	processing: 'Processing',
	complete: 'Complete',
	duplicate: 'Duplicate',
	error: 'Error',
};

const statusToColorMap = {
	received: grey[500],
	processing: orange[500],
	complete: green[500],
	duplicate: orange[700],
	error: red[500],
};

const styles = (theme) => ({
	...getSharedStyles(theme),
	button: {
		verticalAlign: 'middle',
		padding: '8px 10px',
	},
	typeChipInvoice: {
		backgroundColor: blue[100],
	},
	typeChipDelivery: {
		backgroundColor: green[100],
	},
	testChip: {
		backgroundColor: grey[300],
		marginLeft: 4,
		fontWeight: 'bold',
	},
	payloadPre: {
		fontSize: 12,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
});

const columns = [
	{
		sortId: 'superiorSubmissionId',
		displayId: 'superiorSubmissionId',
		label: 'ID',
	},
	{
		sortId: 'dateCreated',
		displayId: 'dateCreatedFormatted',
		label: 'Received',
	},
	{
		sortId: 'submissionType',
		displayId: 'typeDisplay',
		label: 'Type',
	},
	{
		sortId: 'externalKey',
		displayId: 'externalKey',
		label: 'Invoice / Ticket #',
	},
	{
		sortId: 'summary',
		displayId: 'summaryDisplay',
		label: 'Summary',
	},
	{
		sortId: 'processingStatus',
		displayId: 'statusDisplay',
		label: 'Status',
	},
	{
		sortId: 'documents',
		displayId: 'documentsDisplay',
		label: 'Documents',
	},
	{
		sortId: 'payload',
		displayId: 'payloadDisplay',
		label: 'Payload',
	},
	{
		sortId: 'linked',
		displayId: 'linkedDisplay',
		label: 'Linked Ingestion',
	},
];

function summarizePayload(superiorSubmission) {
	const { submissionType, rawPayload } = superiorSubmission;
	if (!rawPayload) {
		return '-';
	}

	if (submissionType === 'invoice') {
		const loads = rawPayload.loads || [];
		const stationCodes = [];
		const orderNumbers = [];
		loads.forEach((load) => {
			(load.stops || []).forEach((stop) => stationCodes.push(stop.stationCode));
			if (load.orderNumber) {
				orderNumbers.push(load.orderNumber);
			}
		});
		return (
			`${loads.length} load${loads.length === 1 ? '' : 's'} / ` +
			`${_.uniq(stationCodes).join(', ')} / ` +
			`Orders: ${_.uniq(orderNumbers).join(', ')} / ` +
			`$${rawPayload.totalDue}`
		);
	}

	const bols = rawPayload.bols || [];
	return (
		`${rawPayload.stationCode} / Order: ${rawPayload.orderNumber} / ` +
		`${bols.length} BOL${bols.length === 1 ? '' : 's'} / ` +
		`${(rawPayload.pages || []).length} page(s)`
	);
}

class SuperiorSubmissions extends Component {
	constructor(props) {
		super(props);

		this.state = {
			order: 'desc',
			orderBy: 'superiorSubmissionId',
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
			payloadPoppedUp: null,
		};
	}

	componentDidMount() {
		const { superiorSubmissions, superiorSubmissionSearch } = this.props;

		if (!superiorSubmissions) {
			superiorSubmissionSearch({
				afterDateTime: moment().subtract(2, 'months').format('YYYY-MM-DD'),
			});
		} else {
			this.formatRows();
		}
	}

	componentDidUpdate(prevProps) {
		const { superiorSubmissions } = this.props;
		if (prevProps.superiorSubmissions !== superiorSubmissions) {
			this.formatRows();
		}
	}

	formatRows = () => {
		const { superiorSubmissions, classes } = this.props;

		const rows = [];
		superiorSubmissions.forEach((superiorSubmission) => {
			const {
				superiorSubmissionId,
				submissionType,
				isTest,
				dateCreated,
				processingStatus,
				processingStatusMessage,
				documentResourceIds,
				apIngestionId,
				ocrIngestionId,
				linkedOcrIngestionStatus,
				linkedApIngestionStatus,
			} = superiorSubmission;

			const row = { ...superiorSubmission };
			row.key = superiorSubmissionId;
			row.dateCreatedFormatted = dateCreated
				? moment(dateCreated).format('M/D/YY h:mm a')
				: '-';

			const isInvoice = submissionType === 'invoice';
			row.typeDisplay = (
				<span>
					<Chip
						size="small"
						label={isInvoice ? 'Invoice' : 'Delivery Ticket'}
						className={isInvoice ? classes.typeChipInvoice : classes.typeChipDelivery}
					/>
					{isTest ? <Chip size="small" label="TEST" className={classes.testChip} /> : null}
				</span>
			);

			row.summaryDisplay = summarizePayload(superiorSubmission);

			row.statusDisplay = (
				<div style={{ color: statusToColorMap[processingStatus], fontWeight: 'bold' }}>
					{statusToDisplayMap[processingStatus] || processingStatus}
					{processingStatusMessage && (
						<div
							style={{
								color: grey[700],
								fontWeight: 'normal',
								fontSize: 12,
								maxWidth: 320,
								whiteSpace: 'normal',
							}}
						>
							{processingStatusMessage}
						</div>
					)}
				</div>
			);

			row.documentsDisplay = _.isEmpty(documentResourceIds) ? (
				'-'
			) : (
				<Button
					onClick={() => {
						this.handleDocumentsClick(documentResourceIds);
					}}
				>
					View
				</Button>
			);

			row.payloadDisplay = (
				<Button
					onClick={() => {
						this.handlePayloadClick(superiorSubmission);
					}}
				>
					View
				</Button>
			);

			if (apIngestionId) {
				row.linkedDisplay = (
					<div>
						<a
							href="/apAutomation/apIngestions"
							style={{ textDecoration: 'none' }}
							target="_blank"
							rel="noreferrer noopener"
						>
							<strong>AP Ingestion #{apIngestionId}</strong>
						</a>
						{linkedApIngestionStatus && (
							<div style={{ fontSize: 12, color: grey[700] }}>{linkedApIngestionStatus}</div>
						)}
					</div>
				);
			} else if (ocrIngestionId) {
				row.linkedDisplay = (
					<div>
						<a
							href="/ocr/ocrIngestions"
							style={{ textDecoration: 'none' }}
							target="_blank"
							rel="noreferrer noopener"
						>
							<strong>AR Ingestion #{ocrIngestionId}</strong>
						</a>
						{linkedOcrIngestionStatus && (
							<div style={{ fontSize: 12, color: grey[700] }}>{linkedOcrIngestionStatus}</div>
						)}
					</div>
				);
			} else {
				row.linkedDisplay = '-';
			}

			rows.push(row);
		});
		this.setState({ rows });
	};

	handleDocumentsClick = (resourceIds) => {
		this.setState({
			resourceIdsPoppedUp: resourceIds,
			isResourcePopupOpen: true,
		});
	};

	handleCloseResourcePopup = () => {
		this.setState({
			isResourcePopupOpen: false,
			resourceIdsPoppedUp: [],
		});
	};

	handlePayloadClick = (superiorSubmission) => {
		this.setState({ payloadPoppedUp: superiorSubmission });
	};

	handleClosePayload = () => {
		this.setState({ payloadPoppedUp: null });
	};

	render() {
		const { classes, resourceMap } = this.props;
		const { rows, order, orderBy, payloadPoppedUp } = this.state;

		if (!rows) {
			return <div>Loading the Superior data queue...</div>;
		}

		return (
			<Grid container spacing={3}>
				<Grid item xs={12}>
					<Grid container spacing={3}>
						<Grid item xs={8}>
							<Typography variant="h5" align="left" gutterBottom className={classes.title}>
								<span>Superior Data Queue</span>
							</Typography>
						</Grid>
					</Grid>

					<Paper className={classes.paper}>
						<EnhancedTable
							columns={columns}
							rows={rows}
							defaultOrder={order}
							defaultOrderBy={orderBy}
						/>

						<ResourcePopup
							open={this.state.isResourcePopupOpen}
							onClose={this.handleCloseResourcePopup}
							resourceIds={this.state.resourceIdsPoppedUp}
							resourceMap={resourceMap}
						/>

						<Dialog
							open={!!payloadPoppedUp}
							onClose={this.handleClosePayload}
							maxWidth="md"
							fullWidth
						>
							<DialogTitle>
								Raw Payload
								{payloadPoppedUp ? ` — Submission #${payloadPoppedUp.superiorSubmissionId}` : ''}
							</DialogTitle>
							<DialogContent>
								<pre className={classes.payloadPre}>
									{payloadPoppedUp
										? JSON.stringify(payloadPoppedUp.rawPayload, null, 2)
										: ''}
								</pre>
							</DialogContent>
						</Dialog>
					</Paper>
				</Grid>
			</Grid>
		);
	}
}

SuperiorSubmissions.propTypes = {
	classes: PropTypes.object.isRequired,
	superiorSubmissionSearch: PropTypes.func.isRequired,
	superiorSubmissions: PropTypes.array,

	showErrors: PropTypes.func.isRequired,
	resourceMap: PropTypes.object,

	history: PropTypes.object,
	theme: PropTypes.object.isRequired,
	authUser: PropTypes.object,
};

export default _.flow([
	withStyles(styles, { withTheme: true }),
	AuthUserContainer,
	SuperiorSubmissionsContainer,
])(SuperiorSubmissions);
